import React, { useContext, useState, useEffect } from 'react';
import { GoogleLogin, GoogleOAuthProvider } from '@react-oauth/google';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { useNavigate, useLocation } from 'react-router';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Toaster, toast } from 'sonner';

import { ROLES } from '../../../App';
import { AuthenticationContext } from '../../../context/Authentication';
import useAuth from '../../../hooks/useAuth';
import { getRedirectPath } from '../../../util/AuthRedirect';

import { Input } from '../../businessSignUp/components/Input';
import { Label } from '../../businessSignUp/components/Label';
import Button from '../../../components/Button';
import { Eye, EyeOff, LogIn } from 'lucide-react';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';

const loginSchema = z.object({
  userEmail: z
    .string()
    .min(1, 'Email is required')
    .email('Valid email is required'),
  userPassword: z.string().min(1, 'Password is required'),
});

function Login() {
  const authentication = useContext(AuthenticationContext);
  const { setAuth } = useAuth();
  const [userInfo, setUserInfo] = useState({});
  const [showloginButton, setShowloginButton] = useState(true);
  const server_url = process.env.REACT_APP_API_URI;
  const googleClientID = process.env.REACT_APP_GOOGLE_OAUTH_CLIENT_ID;
  const [showPassword, setShowPassword] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const [sessionExpiredMessage, setSessionExpiredMessage] = useState('');

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(loginSchema),
    mode: 'onChange',
    defaultValues: {
      userEmail: '',
      userPassword: '',
    },
  });

  useEffect(() => {
    if (location.state?.expired) {
      setSessionExpiredMessage('Your session has expired. Please login again.');
      // Clear the state to avoid showing message on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  // Google Login check
  const checkLogin = async (user) => {
    try {
      const response = await axios.post(
        `${server_url}/user/google-login`,
        JSON.stringify({
          name: user.name,
          email: user.email,
        }),
        {
          headers: {
            'Content-Type': 'application/json',
          },
        },
      );
      if (response.data.isLoggedIn === true) {
        const data = jwtDecode(response.data.token);
        const { token } = response.data;
        const roles = data.role;
        const { email } = data;
        setAuth({ email, roles, token });
        authentication.login();

        localStorage.setItem('loginToken', response.data.token);
        toast.success('Login successful!');
        navigate(getRedirectPath({ location, roles, adminRole: ROLES.Admin }));
      } else {
        toast.error(response.data.msg);
      }
    } catch (err) {
      console.log(err);
      toast.error('Google login failed.');
    }
  };

  const handleLoginFormSubmit = async (dataForm) => {
    try {
      const response = await axios.post(
        `${server_url}/user/login`,
        JSON.stringify(dataForm),
        {
          headers: {
            'Content-Type': 'application/json',
          },
        },
      );
      if (response.data.isLoggedIn === true) {
        const data = jwtDecode(response.data.token);
        const { token } = response.data;
        const roles = data.role;
        const { email } = data;
        setAuth({ email, roles, token });

        authentication.login();

        localStorage.setItem('loginToken', response.data.token);

        toast.success('Welcome back!');
        navigate(
          getRedirectPath({
            location,
            roles: data.role,
            adminRole: ROLES.Admin,
          }),
        );
      } else {
        toast.error(response.data.msg || 'Invalid credentials');
      }
    } catch (err) {
      console.log(err);
      toast.error('An error occurred during login.');
    }
  };

  return (
    <>
      <Toaster theme="dark" position="top-right" richColors />
      <div className="flex flex-col items-center justify-center min-h-[calc(100vh-100px)] py-12 bg-primary font-montserrat px-4">
        <div className="w-full max-w-md bg-[#111827] shadow-2xl rounded-3xl overflow-hidden border border-slate-800">
          <div className="bg-gradient-to-br from-[#081325] to-[#112140] p-8 md:p-10 text-white relative flex flex-col items-center">
            <button
              type="button"
              onClick={() => navigate('/')}
              className="absolute left-6 top-6 flex items-center text-sm gap-1 text-white/70 hover:text-white transition-colors"
            >
              <ArrowBackIosNewIcon fontSize="small" />
              Back
            </button>
            <h1 className="text-3xl md:text-4xl font-bold text-center mt-6">
              Welcome.
            </h1>
            <p className="mt-4 text-blue-200 text-center max-w-xs text-sm">
              Login to access your KnowQuest account.
            </p>
          </div>

          <div className="p-8 md:p-10">
            {sessionExpiredMessage && (
              <div className="bg-slate-800/80 border-l-4 border-yellow-500 text-yellow-500 p-4 mb-6 rounded-md text-sm cursor-default">
                <p className="font-bold">Session Expired</p>
                <p>{sessionExpiredMessage}</p>
              </div>
            )}

            <div className="flex justify-center mb-6 w-full">
              <GoogleOAuthProvider clientId={googleClientID}>
                {showloginButton ? (
                  <GoogleLogin
                    theme="outline"
                    size="large"
                    shape="pill"
                    logo_alignment="center"
                    text="continue_with"
                    width="330"
                    onSuccess={(credentialResponse) => {
                      const user = jwtDecode(credentialResponse.credential);
                      setUserInfo(user);
                      setShowloginButton(false);
                      checkLogin(user);
                    }}
                    onError={() => {
                      console.log('Login Failed');
                      toast.error('Google login failed');
                    }}
                  />
                ) : null}
              </GoogleOAuthProvider>
            </div>

            <div className="flex items-center gap-4 mb-6">
              <div className="h-px bg-slate-800 flex-1"></div>
              <span className="text-sm text-slate-400 font-medium">
                Or log in with email
              </span>
              <div className="h-px bg-slate-800 flex-1"></div>
            </div>

            <form
              onSubmit={handleSubmit(handleLoginFormSubmit)}
              className="space-y-6 animate-in slide-in-from-right-4 duration-500"
            >
              <div className="space-y-2">
                <Label htmlFor="userEmail">
                  Email Address <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="userEmail"
                  type="email"
                  placeholder="Enter email"
                  {...register('userEmail')}
                />
                {errors.userEmail && (
                  <span className="text-xs text-red-500 font-medium">
                    {errors.userEmail.message}
                  </span>
                )}
              </div>

              <div className="space-y-2 relative">
                <Label htmlFor="userPassword">
                  Password <span className="text-red-500">*</span>
                </Label>
                <div className="relative">
                  <Input
                    id="userPassword"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Password"
                    {...register('userPassword')}
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-3.5 text-slate-400 hover:text-slate-300"
                  >
                    {showPassword ? (
                      <Eye className="w-5 h-5" />
                    ) : (
                      <EyeOff className="w-5 h-5" />
                    )}
                  </button>
                </div>
                {errors.userPassword && (
                  <span className="text-xs text-red-500 font-medium">
                    {errors.userPassword.message}
                  </span>
                )}
              </div>

              <div className="text-center pt-2">
                <p className="text-slate-400 text-xs mb-4">
                  We'll never share your email with anyone else.
                </p>
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full flex items-center justify-center gap-2 rounded-xl text-white bg-blue-600 hover:bg-blue-700 transition-all font-semibold py-3 shadow-lg shadow-blue-500/30"
                >
                  {isSubmitting ? 'Logging in...' : 'Login'}{' '}
                  <LogIn className="w-5 h-5 ml-1" />
                </Button>
              </div>
            </form>

            <div className="mt-8 space-y-3 text-sm text-center">
              <div>
                <Link
                  to="/forgot-password"
                  className="text-blue-400 hover:text-blue-300 transition-colors font-medium"
                >
                  Forgot Password?
                </Link>
              </div>
              <div className="text-slate-400">
                Business account?{' '}
                <Link
                  to="/businesslogin"
                  className="text-blue-400 hover:text-blue-300 transition-colors font-medium"
                >
                  Login here.
                </Link>
              </div>
              <div className="text-slate-400">
                No account?{' '}
                <Link
                  to="/register"
                  className="text-blue-400 hover:text-blue-300 transition-colors font-medium"
                >
                  Let's Register.
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default Login;
